import React from 'react'
import { FormikProps } from 'formik'
import { IntlShape } from 'react-intl'
import { TourDetails, TourEvent } from '../../../core/_models'
import { deleteTourEvent } from '../../../core/_requests'
import { ItineraryModal } from './ItineraryModal'
import { useConfirm } from '../../../../../../components/ConfirmProvider'

interface ItineraryProps {
  formik: FormikProps<TourDetails>
  showForm: boolean
  setShowForm: (show: boolean) => void
  newItinerary: TourEvent
  setNewItinerary: React.Dispatch<React.SetStateAction<TourEvent>>
  handleAddItinerary: (event: TourEvent) => void
  intl: IntlShape
  events: TourEvent[]
}

export const Itinerary: React.FC<ItineraryProps> = ({
  formik,
  showForm,
  setShowForm,
  newItinerary,
  setNewItinerary,
  handleAddItinerary: onAddItinerary,
  intl,
  events
}) => {
  const confirm = useConfirm()
  const handleAddItinerary = async (data: TourEvent) => {
    try {
      if (data.title.trim() && data.content.trim()) {
        const tourId = formik.values.id
        if (!tourId) {
          console.error('Tour ID is required')
          return
        }

        // Remove unwanted properties for both POST and PATCH
        const { _id, createdAt, updatedAt, __v, ...cleanEventData } = data

        // Extract just the image ID if image is an object
        if (cleanEventData.image && typeof cleanEventData.image === 'object' && '_id' in cleanEventData.image) {
          cleanEventData.image = String(cleanEventData.image._id)
        }

        // Call the parent handler with cleaned data
        onAddItinerary(cleanEventData)

        // Reset form
        setNewItinerary({
          title: '',
          content: '',
          description: '',
          startTime: '',
          endTime: '',
          day: '',
          order: (events?.length || 0) + 1,
          image: undefined
        } as TourEvent)
        setShowForm(false)
      }
    } catch (error) {
      console.error('Error handling itinerary:', error)
    }
  }

  const handleDeleteItinerary = async (eventId: string) => {
    const ok = await confirm({ message: intl.formatMessage({ id: 'TOUR.ITINERARY.DELETE_CONFIRM' }) })
    if (!ok) return
    try {
      await deleteTourEvent(eventId)
      // Update formik state after successful deletion
      const updatedEvents = events.filter(event => event._id !== eventId)
      formik.setFieldValue('events', updatedEvents)
    } catch (error) {
      console.error('Error deleting itinerary:', error)
    }
  }

  return (
    <div className='card card-flush py-4'>
      <div className='card-header'>
        <div className='card-title'>
          <h2>{intl.formatMessage({ id: 'TOUR.FIELDS.ITINERARY' })}</h2>
        </div>
        <div className='card-toolbar'>
          <button
            type='button'
            className='btn btn-sm btn-light-primary'
            onClick={() => {
              setNewItinerary({
                title: '',
                content: '',
                description: '',
                startTime: '',
                endTime: '',
                day: '',
                order: (events?.length || 0) + 1,
                image: undefined
              } as TourEvent)
              setShowForm(true)
            }}
          >
            {intl.formatMessage({ id: 'TOUR.ACTIONS.ADD_ELEMENT' })}
          </button>
        </div>
      </div>
      <div className='card-body pt-0'>
        {/* Modal Form */}
        <ItineraryModal
          show={showForm}
          onHide={() => setShowForm(false)}
          formik={formik}
          newItinerary={newItinerary}
          setNewItinerary={setNewItinerary}
          handleAddItinerary={handleAddItinerary}
          intl={intl}
          events={events}
        />

        {/* List */}
        <div className='py-2'>
          {events?.map((event: TourEvent, index: number) => (
            <div key={index} className='d-flex flex-stack py-4'>
              <div className='d-flex align-items-center'>
                {event.image && (
                  <div className='me-4'>
                    <img
                      src={typeof event.image === 'string' 
                        ? (event.image.includes('/images/') 
                            ? (process.env.REACT_APP_API_URL_IMAGE || '') + event.image 
                            : event.image)
                        : (event.image.url.includes('/images/') 
                            ? (process.env.REACT_APP_API_URL_IMAGE || '') + event.image.url 
                            : event.image.url)}
                      alt={event.title}
                      className='w-300px h-220px object-fit-cover rounded-3'
                    />
                  </div>
                )}
                <div className='ms-5 flex-grow-1'>
                  <div className='fs-5 fw-bold text-gray-900 text-hover-primary mb-2' style={{ height: '30px', overflow: 'hidden', lineHeight: '30px' }}>
                    {event.title}
                  </div>
                  
                  <div className='mb-1'>
                    <span className='text-muted fw-bold fs-6'>Content:</span>
                  </div>
                  <div className='fw-semibold w-75 text-muted mb-2' style={{ height: '80px', overflow: 'hidden' }} dangerouslySetInnerHTML={{ __html: event.content }} />
                  
                  {event.description && (
                    <>
                      <div className='mb-1'>
                        <span className='text-muted fw-bold fs-6'>Description:</span>
                      </div>
                      <div className='text-muted fs-7 mb-2' style={{ height: '40px', overflow: 'hidden', maxWidth: '80%' }} dangerouslySetInnerHTML={{ __html: event.description }} />
                    </>
                  )}
                  
                  <div className='d-flex align-items-center mt-1'>
                    {(event.startTime || event.endTime) && (
                      <span className='badge badge-light-primary me-2'>
                        {event.startTime || '--:--'} - {event.endTime || '--:--'}
                      </span>
                    )}
                    {event.day && (
                      <span className='badge badge-light-info me-2'>
                        Day {event.day}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className='d-flex'>
                <button
                  type='button'
                  className='btn btn-light btn-sm me-1'
                  onClick={() => {
                    setNewItinerary(event)
                    setShowForm(true)
                  }}
                >
                  {intl.formatMessage({ id: 'ACTION.EDIT' })}
                </button>
                <button
                  type='button'
                  className='btn btn-light-danger btn-sm'
                  onClick={() => {
                    if (event._id) {
                      handleDeleteItinerary(event._id)
                    }
                  }}
                >
                  {intl.formatMessage({ id: 'ACTION.DELETE' })}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
} 